import { Text, Tooltip } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { IconElementTag } from '~/packages/common-frontend/components/Tag';
import { useQueryParams } from '~/packages/common-frontend/hooks/useQueryParams';
import { Link } from '~/valleyBank/domains/components/Link';

const LOAN_ACCOUNT_URL_PARAM = 'loanAccount';

const generateLoanAccountQueryParam = (loanAccountId: string): string => {
  return `?${LOAN_ACCOUNT_URL_PARAM}=${loanAccountId}`;
};

const generateLoanAccountUrl = ({
  path,
  loanAccountId,
}: {
  path: string;
  loanAccountId: string;
}): string => {
  return `${path}${generateLoanAccountQueryParam(loanAccountId)}`;
};

type LoanAccountLinkProps = {
  children: React.ReactNode;
  loanAccountId: string;
  path: string;
  scrollToTop?: boolean;
};
const LoanAccountLink = ({
  children,
  loanAccountId,
  path,
  scrollToTop = true,
}: LoanAccountLinkProps) => {
  return (
    <Tooltip label="Loan Account Number" placement="auto" textAlign="center">
      <span>
        <Link
          to={generateLoanAccountUrl({
            path,
            loanAccountId,
          })}
          onClick={
            scrollToTop
              ? () => {
                  window.scrollTo(0, 0);
                }
              : undefined
          }
        >
          {children}
        </Link>
      </span>
    </Tooltip>
  );
};

type LoanAccountSummaryLinkProps = {
  children: React.ReactNode;
  loanAccountId: string;
};
export const LoanAccountSummaryLink = ({
  children,
  loanAccountId,
}: LoanAccountSummaryLinkProps) => (
  <LoanAccountLink
    loanAccountId={loanAccountId}
    path="/valley-bank/loan-accounts/summary"
  >
    {children}
  </LoanAccountLink>
);

type LoanAccountTagProps = { loanAccountId: string };
export const LoanAccountTag = ({ loanAccountId }: LoanAccountTagProps) => {
  return (
    <IconElementTag icon="building-columns">
      <LoanAccountSummaryLink loanAccountId={loanAccountId}>
        <Text fontWeight="bold">{loanAccountId}</Text>
      </LoanAccountSummaryLink>
    </IconElementTag>
  );
};

export const useLoanAccountUrlParam = (): {
  loanAccountId: string | null;
  setLoanAccountId: (newLoanAccountId: string | null) => void;
  generateLoanAccountUrl: (path: string) => string;
} => {
  const navigate = useNavigate();
  const { pathname: currentPath } = useLocation();
  const query = useQueryParams();
  const loanAccountId = query.get(LOAN_ACCOUNT_URL_PARAM);

  const setLoanAccountId = useCallback(
    (newLoanAccountId: string | null) => {
      // @jendevelops TODO: add error message when loan account id doesn't exist
      if (newLoanAccountId) {
        navigate(
          generateLoanAccountUrl({
            path: currentPath,
            loanAccountId: newLoanAccountId,
          }),
        );
      }
    },
    [currentPath, navigate],
  );

  const _generateLoanAccountUrl = useCallback(
    (path: string) =>
      generateLoanAccountUrl({ path, loanAccountId: loanAccountId || '' }),
    [loanAccountId],
  );

  return {
    loanAccountId,
    setLoanAccountId,
    generateLoanAccountUrl: _generateLoanAccountUrl,
  };
};
